"use client";

import { useEffect, useRef, useState } from "react";
import { HStack } from "@astryxdesign/core/HStack";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { Link } from "@astryxdesign/core/Link";
import { CONSENT_LINKS } from "@/lib/consent/constants";
import {
  clearCookieBannerOffset,
  syncCookieBannerOffset,
} from "@/lib/consent/cookie-banner-offset";

type CookieConsentBannerProps = {
  initialAccepted: boolean;
};

/** Глобальный баннер cookies — короткий текст как на smartbiznes.org. */
export function CookieConsentBanner({
  initialAccepted,
}: CookieConsentBannerProps) {
  const [accepted, setAccepted] = useState(initialAccepted);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (accepted) return;

    const el = bannerRef.current;
    if (!el) return;

    const sync = () => syncCookieBannerOffset(el);

    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    window.addEventListener("resize", sync);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
      clearCookieBannerOffset();
    };
  }, [accepted]);

  if (accepted) return null;

  async function accept() {
    setPending(true);
    setError(null);
    try {
      const res = await fetch("/api/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ types: ["cookies"], accepted: true }),
      });
      if (!res.ok) {
        throw new Error("Не удалось сохранить согласие");
      }
      setAccepted(true);
    } catch {
      setError("Не удалось сохранить согласие. Попробуйте ещё раз.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      ref={bannerRef}
      data-ix-cookie-consent-banner=""
      role="dialog"
      aria-label="Согласие на использование cookies"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-body/95 p-4 shadow-lg backdrop-blur-md sm:p-5"
    >
      <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <Text type="supporting" color="secondary" className="leading-relaxed">
            Этот сайт использует файлы Cookie для хранения данных. Продолжая
            использовать сайт, вы даёте своё согласие на работу с этими файлами.{" "}
            <Link href={CONSENT_LINKS.cookies}>Политика Cookies</Link>
          </Text>
          {error ? (
            <Text
              type="supporting"
              className="mt-2 text-[var(--color-text-danger,#b91c1c)]"
            >
              {error}
            </Text>
          ) : null}
        </div>
        <HStack gap={3} className="shrink-0">
          <Button
            label="Хорошо"
            variant="primary"
            size="lg"
            isLoading={pending}
            onClick={() => void accept()}
          />
        </HStack>
      </div>
    </div>
  );
}
